import React, { useState, useEffect } from 'react';
import { BookOpen, Gem, Lock } from 'lucide-react';
import BuyResult from './BuyResult';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const CollectionPanel = ({ gameId, onRefresh }) => {
  const [collection, setCollection] = useState([]);
  const [stats, setStats] = useState({});
  const [buying, setBuying] = useState(false);
  const [buyResult, setBuyResult] = useState(null);

  useEffect(() => { loadCollection(); }, [gameId]);

  const loadCollection = async () => {
    try {
      const res = await fetch(`${API}/api/store/collection/${gameId}`);
      if (res.ok) {
        const data = await res.json();
        setCollection(data.collection || []);
        setStats(data);
      }
    } catch (e) { console.error(e); }
  };

  const handleBuy = async (type) => {
    setBuying(true);
    try {
      const res = await fetch(`${API}/api/store/collection/buy-fragment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, fragment_type: type }),
      });
      if (res.ok) {
        const data = await res.json();
        setBuyResult(data);
        setTimeout(() => setBuyResult(null), 3500);
        if (data.success) { loadCollection(); onRefresh(); }
      }
    } catch (e) { console.error(e); }
    setBuying(false);
  };

  return (
    <div data-testid="collection-panel">
      <div className="flex items-center gap-2 mb-1">
        <BookOpen size={16} className="text-[#C9A84C]" />
        <h3 className="font-['Cormorant_Garamond'] text-[#C9A84C] text-lg font-bold">CLASSIFIED DOSSIERS</h3>
      </div>
      <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[9px] tracking-widest mb-2">
        COLLECT 5 FRAGMENTS TO UNLOCK EACH FILE — {stats.total_unlocked || 0}/{stats.total_characters || 0} UNLOCKED
      </p>

      {/* Buy buttons */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => handleBuy('random')} disabled={buying}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#C9A84C]/30 bg-[#C9A84C]/10 text-[#C9A84C] font-['Outfit'] text-[10px] tracking-wider hover:bg-[#C9A84C]/20 disabled:opacity-40"
          data-testid="buy-random-fragment">
          <Gem size={10} />200 — Random Fragment
        </button>
        <button onClick={() => handleBuy(collection.find(c => !c.unlocked)?.character || 'random')} disabled={buying}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#9F7AEA]/30 bg-[#9F7AEA]/10 text-[#9F7AEA] font-['Outfit'] text-[10px] tracking-wider hover:bg-[#9F7AEA]/20 disabled:opacity-40"
          data-testid="buy-targeted-fragment">
          <Gem size={10} />800 — Targeted Fragment
        </button>
      </div>

      <BuyResult result={buyResult} />

      {/* Collection grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
        {collection.map(c => (
          <div key={c.character} className={`bg-[#111820] border rounded-lg p-3 text-center transition-all ${
            c.unlocked ? 'border-[#C9A84C]/30 bg-[#C9A84C]/5' : 'border-[#1A2535]'
          }`} data-testid={`dossier-${c.character}`}>
            <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center border ${
              c.unlocked ? 'bg-[#C9A84C]/20 border-[#C9A84C]/40' : 'bg-[#1A2535] border-[#1A2535]'
            }`}>
              {c.unlocked
                ? <BookOpen size={16} className="text-[#C9A84C]" />
                : <Lock size={14} className="text-[#E8E0D0]/15" />}
            </div>
            <p className={`font-['Outfit'] text-[10px] font-semibold ${c.unlocked ? 'text-[#C9A84C]' : 'text-[#E8E0D0]/40'}`}>
              {c.character.toUpperCase()}
            </p>
            {/* Progress bar */}
            <div className="h-1 bg-[#1A2535] rounded-full mt-1.5 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{
                width: `${c.progress_pct}%`,
                backgroundColor: c.unlocked ? '#C9A84C' : '#4A7AB5'
              }} />
            </div>
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] mt-1">{c.fragments}/{c.needed}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CollectionPanel;
